import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import face from '../assets/footer/faceb.webp';
import spot from '../assets/footer/spotb.webp';
import yout from '../assets/footer/youtb.webp';
import inst from '../assets/footer/intsb.webp';
import linke from '../assets/footer/linkeb.webp';
import logo from '../assets/footer/Logo-Completo-Blanco.webp';
import { Link } from 'react-router-dom';
import ChatButton from '../Components/ChatButton/ChatButton';

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

export const Footer = () => {
  const { t } = useTranslation();
  const [deviceType, setDeviceType] = useState('desktop');
  const [showModal, setShowModal] = useState(false);

  // Función para determinar el tipo de dispositivo basado en el ancho de la ventana
  const determineDeviceType = () => {
    const width = window.innerWidth;
    if (width < 768) {
      return 'mobile';
    } else if (width >= 768 && width < 1024) {
      return 'tablet';
    } else {
      return 'desktop';
    }
  };

  useEffect(() => {
    // Establecer el tipo de dispositivo inicial
    setDeviceType(determineDeviceType());

    // Función para actualizar el tipo de dispositivo cuando cambia el tamaño de la ventana
    const handleResize = () => {
      setDeviceType(determineDeviceType());
    };

    // Añadir event listener para el cambio de tamaño
    window.addEventListener('resize', handleResize);

    // Limpiar event listener al desmontar
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleOpenModal = () => {
    setShowModal(true);
  };

  // Lista común de enlaces para reutilizar
  const solutionsLinks = (
    <ul className="space-y-2">
      <li>
        <a href="/servicios/1" className="text-xs hover:text-gray-300">
          {t('footer.solutions.wealth')}
        </a>
      </li>
      <li>
        <a href="/servicios/3" className="text-xs hover:text-gray-300">
          {t('footer.solutions.legacy')}
        </a>
      </li>
      <li>
        <a href="/servicios/4" className="text-xs hover:text-gray-300">
          {t('footer.solutions.business')}
        </a>
      </li>
      <li>
        <a href="/servicios/5" className="text-xs hover:text-gray-300">
          {t('footer.solutions.capitalBoost')}
        </a>
      </li>
      <li>
        <a href="/servicios/6" className="text-xs hover:text-gray-300">
          {t('footer.solutions.educationHorizon')}
        </a>
      </li>
      <li>
        <a href="/servicios/7" className="text-xs hover:text-gray-300">
          {t('footer.solutions.secureFuture')}
        </a>
      </li>
    </ul>
  );

  const usefulLinks = (
    <ul className="space-y-2">
      <li>
        <a href="/nosotros" className="text-xs hover:text-gray-300">
          {t('footer.useful.aboutUs')}
        </a>
      </li>
      <li>
        <a href="/hub" className="text-xs hover:text-gray-300">
          {t('footer.useful.newsArticles')}
        </a>
      </li>
      <li>
        <a
          href="https://grewhubtalks.com/"
          className="text-xs hover:text-gray-300"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('footer.useful.ourPodcast')}
        </a>
      </li>
    </ul>
  );

  const connectLinks = (
    <ul className="space-y-2">
      <li>
        <a
          href="https://intranet.grewhub.com/welcome"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs hover:text-gray-300"
        >
          {t('footer.connect.clientLogin')}
        </a>
      </li>
      <li>
        <button onClick={handleOpenModal} className="text-xs hover:text-gray-300 text-left">
          {t('footer.connect.letsTalk')}
        </button>
      </li>
    </ul>
  );

  const socialIcons = (
    <div className="flex space-x-4">
      <a
        href="https://www.facebook.com/GrewHubInvestments"
        aria-label="Facebook"
        target="_blank"
        rel="noopener noreferrer"
        className="hover:opacity-80"
      >
        <img src={face} alt="Facebook" className="w-5 h-5" />
      </a>
      <a
        href="https://open.spotify.com/show/3YzjONPuQsWnIXcGtfHQDj"
        aria-label="Spotify"
        target="_blank"
        rel="noopener noreferrer"
        className="hover:opacity-80"
      >
        <img src={spot} alt="Spotify" className="w-5 h-5" />
      </a>
      <a
        href="https://www.youtube.com/@Grewhub-Talks"
        aria-label="YouTube"
        target="_blank"
        rel="noopener noreferrer"
        className="hover:opacity-80"
      >
        <img src={yout} alt="YouTube" className="w-5 h-5" />
      </a>
      <a
        href="https://www.instagram.com/grewhub/"
        aria-label="Instagram"
        target="_blank"
        rel="noopener noreferrer"
        className="hover:opacity-80"
      >
        <img src={inst} alt="Instagram" className="w-5 h-5" />
      </a>
      <a
        href="https://www.linkedin.com/company/69206769/admin/dashboard/"
        aria-label="LinkedIn"
        target="_blank"
        rel="noopener noreferrer"
        className="hover:opacity-80"
      >
        <img src={linke} alt="LinkedIn" className="w-5 h-5" />
      </a>
    </div>
  );

  const legalLinks =
    deviceType === 'mobile' ? (
      <div className="flex flex-wrap justify-center gap-2 text-[10px]">
        <a href="/privacidad" className="hover:text-gray-300">
          {t('footer.legal.privacy')}
        </a>
        <a href="/condiciones-de-uso" className="hover:text-gray-300">
          {t('footer.legal.terms')}
        </a>
      </div>
    ) : (
      <div className="flex flex-wrap justify-start gap-4 text-[10px]">
        <a href="/privacidad" className="hover:text-gray-300">
          {t('footer.legal.privacy')}
        </a>
        <span className="inline">|</span>
        <a href="/condiciones-de-uso" className="hover:text-gray-300">
          {t('footer.legal.terms')}
        </a>
      </div>
    );

  const copyright = (
    <div className={`mt-4 text-[10px] ${deviceType === 'mobile' ? 'text-center' : 'text-left'}`}>
      {t('footer.copyright')}
    </div>
  );

  // Renderizado basado en el tipo de dispositivo
  if (deviceType === 'mobile') {
    // Versión para móvil
    return (
      <footer className="bg-[#152b5a] text-white py-8 px-4">
        <ChatButton isOpen={showModal} setIsOpen={setShowModal} showTemaSelector={false} />

        {/* Contenido principal del footer */}
        <div className="flex flex-col">
          {/* Logo y redes sociales en móvil */}
          <div className="flex justify-center mb-8">
            <div className="flex flex-col items-center">
              <Link to="/#" onClick={scrollToTop}>
                <img src={logo} alt="GrewHub Investments" className="w-36" />
              </Link>

              {/* Redes sociales debajo del logo */}
              <div className="mt-4">{socialIcons}</div>
            </div>
          </div>

          {/* Columna SOLUCIONES */}
          <div className="mb-8">
            <h3 className="text-sm font-semibold mb-3">{t('footer.titles.solutions')}</h3>
            {solutionsLinks}
          </div>

          {/* Columna ENLACES ÚTILES */}
          <div className="mb-8">
            <h3 className="text-sm font-semibold mb-3">{t('footer.titles.useful')}</h3>
            {usefulLinks}
          </div>

          {/* Columna CONÉCTATE CON NOSOTROS */}
          <div className="mb-8">
            <h3 className="text-sm font-semibold mb-3">{t('footer.titles.connect')}</h3>
            {connectLinks}
          </div>
        </div>

        {/* Links legales */}
        <div className="mt-8 pt-4 border-t border-white/20">
          {legalLinks}
          {copyright}
        </div>
      </footer>
    );
  } else if (deviceType === 'tablet') {
    // Versión para tablet
    return (
      <footer className="bg-[#152b5a] text-white py-8 px-8">
        <ChatButton isOpen={showModal} setIsOpen={setShowModal} showTemaSelector={false} />

        <div className="grid grid-cols-12 gap-4">
          {/* Columna izquierda: SOLUCIONES y CONÉCTATE CON NOSOTROS */}
          <div className="col-span-4">
            {/* SOLUCIONES */}
            <div className="mb-8">
              <h3 className="text-sm font-semibold mb-3">{t('footer.titles.solutions')}</h3>
              {solutionsLinks}
            </div>

            {/* CONÉCTATE CON NOSOTROS */}
            <div className="mb-8">
              <h3 className="text-sm font-semibold mb-3">{t('footer.titles.connect')}</h3>
              {connectLinks}
            </div>
          </div>

          {/* Columna central: ENLACES ÚTILES */}
          <div className="col-span-4">
            <div className="mb-8">
              <h3 className="text-sm font-semibold mb-3">{t('footer.titles.useful')}</h3>
              {usefulLinks}
            </div>
          </div>

          {/* Columna derecha: Logo y redes sociales en columna */}
          <div className="col-span-4">
            <div className="flex flex-col items-center">
              {/* Logo encima de las redes sociales */}
              <Link to="/#" onClick={scrollToTop} className="mb-6">
                <img src={logo} alt="GrewHub Investments" className="w-40" />
              </Link>

              {/* Redes sociales debajo del logo */}
              <div className="flex justify-center">{socialIcons}</div>
            </div>
          </div>
        </div>

        {/* Links legales */}
        <div className="mt-8 pt-4 border-t border-white/20">
          {legalLinks}
          {copyright}
        </div>
      </footer>
    );
  } else {
    // Versión para desktop
    return (
      <footer className="bg-[#152b5a] text-white py-8 px-16">
        <ChatButton isOpen={showModal} setIsOpen={setShowModal} showTemaSelector={false} />

        <div className="grid grid-cols-12 gap-2">
          {/* Columna SOLUCIONES */}
          <div className="col-span-3 pr-4">
            <h3 className="text-sm font-semibold mb-3">{t('footer.titles.solutions')}</h3>
            {solutionsLinks}
          </div>

          {/* Columna ENLACES ÚTILES */}
          <div className="col-span-3 pr-4">
            <h3 className="text-sm font-semibold mb-3">{t('footer.titles.useful')}</h3>
            {usefulLinks}
          </div>

          {/* Columna CONÉCTATE CON NOSOTROS */}
          <div className="col-span-3 pr-4">
            <h3 className="text-sm font-semibold mb-3">{t('footer.titles.connect')}</h3>
            {connectLinks}
          </div>

          {/* Logo y redes sociales */}
          <div className="col-span-3 flex justify-end">
            <div className="flex flex-col items-end">
              <Link to="/#" onClick={scrollToTop}>
                <img src={logo} alt="GrewHub Investments" className="w-48" />
              </Link>

              {/* Redes sociales debajo del logo */}
              <div className="mt-4 justify-end">{socialIcons}</div>
            </div>
          </div>
        </div>

        {/* Links legales */}
        <div className="mt-8 pt-4 border-t border-white/20">
          {legalLinks}
          {copyright}
        </div>
      </footer>
    );
  }
};

export default Footer;
